import React, { useEffect, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import { setToastMessage } from "../../store/slices/app/appSlice";
import {
  ClientAccountView,
  useGetCompanyRegistryLinkQuery,
  useLinkCompanyToAccountMutation,
  useListClientAccountsQuery,
  useUpdateClientAccountMutation,
} from "../../store/apis/clientRegistryApi";

interface Props {
  companyId: string;
  canEdit: boolean;
}

/**
 * Los datos de registro de una empresa, dentro de su propia ficha.
 *
 * Qué se puede tocar aquí y qué no:
 *   - El NÚMERO de cliente no se edita nunca. Ya está impreso en propuestas emitidas y
 *     cambiarlo cambiaría el significado de documentos que ya han salido.
 *   - La unidad de negocio y el tipo tampoco: el tipo es la letra C o P del número.
 *   - El sector sí, y las marcas se gestionan en la pantalla del registro, que es donde
 *     tienen su propia tabla.
 */
const CompanyRegistryPanel: React.FC<Props> = ({ companyId, canEdit }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { data, isFetching } = useGetCompanyRegistryLinkQuery(companyId, {
    skip: !companyId,
  });
  const link = data?.data;
  const account = link?.account ?? null;

  const [updateAccount, { isLoading: isSaving }] =
    useUpdateClientAccountMutation();
  const [linkCompany, { isLoading: isLinking }] =
    useLinkCompanyToAccountMutation();

  const [sector, setSector] = useState("");
  useEffect(() => {
    setSector(account?.sector ?? "");
  }, [account?.sector]);

  // El selector de cuenta, solo cuando la empresa no está enlazada.
  const [accountQuery, setAccountQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  useEffect(() => {
    const handle = setTimeout(() => setDebounced(accountQuery), 250);
    return () => clearTimeout(handle);
  }, [accountQuery]);

  const { data: accountsData, isFetching: loadingAccounts } =
    useListClientAccountsQuery(
      { pageSize: 25, search: debounced.trim() || undefined },
      { skip: Boolean(account) }
    );

  const [pickedAccount, setPickedAccount] = useState<ClientAccountView | null>(
    null
  );

  const toast = (message: string, severity: "success" | "error") =>
    dispatch(setToastMessage({ message, severity }));

  const errorMessage = (error: unknown) =>
    (error as { data?: { message?: string } })?.data?.message ??
    t("company.registry.failed", "No se pudo guardar");

  const saveSector = async () => {
    if (!account) return;
    try {
      await updateAccount({
        id: account.id,
        body: { sector: sector.trim() || null },
      }).unwrap();
      toast(t("company.registry.sectorSaved", "Sector guardado"), "success");
    } catch (error: unknown) {
      toast(errorMessage(error), "error");
    }
  };

  const doLink = async () => {
    if (!pickedAccount) return;
    try {
      await linkCompany({
        companyId,
        clientAccountId: pickedAccount.id,
      }).unwrap();
      toast(t("company.registry.linked", "Empresa enlazada"), "success");
      setPickedAccount(null);
    } catch (error: unknown) {
      toast(errorMessage(error), "error");
    }
  };

  const field = (label: string, value: React.ReactNode) => (
    <Box>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="body2">{value}</Typography>
    </Box>
  );

  return (
    <Box
      sx={{
        backgroundColor: theme.palette.background.paper,
        borderRadius: "8px",
        p: 2,
        mb: 0.5,
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ pb: 1, mb: 1 }}
      >
        <Typography
          variant="h6"
          sx={{
            fontFamily: "Nunito",
            fontWeight: 700,
            color: theme.palette.grey[900],
          }}
        >
          {t("company.registry.title", "Registro comercial")}
        </Typography>
        {link && !link.hasCrmCompany && (
          <Tooltip
            title={t(
              "company.registry.noCrmHelp",
              "Esta empresa no está en el CRM, así que no puede enganchar la propuesta a una oportunidad."
            )}
          >
            <Chip
              size="small"
              color="warning"
              variant="outlined"
              icon={<WarningAmberIcon />}
              label={t("company.registry.noCrm", "sin empresa en el CRM")}
            />
          </Tooltip>
        )}
      </Stack>

      {isFetching && (
        <Box textAlign="center" py={2}>
          <CircularProgress size={22} />
        </Box>
      )}

      {!isFetching && !account && (
        <>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {/* Es la causa concreta de que la propuesta llegue al CRM incompleta. */}
            {t(
              "company.registry.unlinked",
              "Esta empresa no está enlazada con ninguna cuenta del registro. Las propuestas que la usen llegarán al CRM sin empresa y sin unidad de cuenta."
            )}
          </Alert>

          {canEdit && (
            <Stack direction={{ xs: "column", md: "row" }} gap={1}>
              <Autocomplete
                size="small"
                sx={{ flex: 1, minWidth: 260 }}
                options={accountsData?.data?.items ?? []}
                value={pickedAccount}
                loading={loadingAccounts}
                filterOptions={(options) => options}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                getOptionLabel={(option) =>
                  `${option.accountCode} — ${option.name}`
                }
                onInputChange={(_, value, reason) => {
                  if (reason === "input") setAccountQuery(value);
                }}
                onChange={(_, value) => setPickedAccount(value)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t(
                      "company.registry.pickAccount",
                      "Cuenta del registro"
                    )}
                    helperText={t(
                      "company.registry.pickHelp",
                      "Busca por nombre o por número de cliente."
                    )}
                  />
                )}
              />
              <Button
                variant="outlined"
                onClick={doLink}
                disabled={!pickedAccount || isLinking}
              >
                {t("company.registry.link", "Enlazar")}
              </Button>
            </Stack>
          )}
        </>
      )}

      {account && (
        <>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                md: "repeat(4, 1fr)",
              },
              gap: 2.5,
              mb: 2,
            }}
          >
            {field(
              t("company.registry.code", "Número de cliente"),
              <Tooltip
                title={t(
                  "company.registry.codeFixed",
                  "No se puede cambiar: ya puede estar impreso en propuestas emitidas."
                )}
              >
                <Box
                  component="span"
                  sx={{ fontFamily: "monospace", fontWeight: 700 }}
                >
                  {account.accountCode}
                </Box>
              </Tooltip>
            )}
            {field(
              t("company.registry.name", "Nombre en el registro"),
              account.name
            )}
            {field(
              t("company.registry.businessUnit", "Unidad"),
              account.businessUnit === "BEAPOLO" ? "BeApolo" : "House Group"
            )}
            {field(
              t("company.registry.type", "Tipo"),
              account.type === "CLIENTE"
                ? t("clientRegistry.type.cliente", "Cliente")
                : t("clientRegistry.type.prospect", "Prospect")
            )}
            {field(
              t("company.registry.represents", "Esta empresa es"),
              link?.clientBrandNumber !== null &&
                link?.clientBrandNumber !== undefined ? (
                <Chip
                  size="small"
                  variant="outlined"
                  label={`${t("clientRegistry.linked.brand", "marca {{n}}", {
                    n: link.clientBrandNumber,
                  })} · ${
                    account.brands.find(
                      (brand) => brand.brandNumber === link.clientBrandNumber
                    )?.name ?? ""
                  }`}
                />
              ) : (
                <Chip
                  size="small"
                  variant="outlined"
                  label={t("clientRegistry.linked.asClient", "el cliente")}
                />
              )
            )}
          </Box>

          <Stack direction="row" gap={1} alignItems="flex-start" mb={2}>
            <TextField
              size="small"
              label={t("company.registry.sector", "Sector")}
              value={sector}
              onChange={(event) => setSector(event.target.value)}
              disabled={!canEdit}
              sx={{ maxWidth: 320 }}
            />
            {canEdit && (
              <Button
                size="small"
                startIcon={<CheckIcon />}
                onClick={saveSector}
                disabled={isSaving || sector === (account.sector ?? "")}
                sx={{ mt: 0.25 }}
              >
                {t("company.registry.saveSector", "Guardar sector")}
              </Button>
            )}
          </Stack>

          <Typography variant="caption" color="text.secondary" display="block">
            {t("company.registry.brands", "Marcas de la cuenta")} (
            {account.brands.length})
          </Typography>
          <Stack direction="row" flexWrap="wrap" gap={0.5} mt={0.5} useFlexGap>
            {account.brands.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {t("company.registry.noBrands", "Sin marcas")}
              </Typography>
            ) : (
              account.brands.map((brand) => (
                <Chip
                  key={brand.id}
                  size="small"
                  variant="outlined"
                  label={`${brand.brandCode} · ${brand.name}`}
                />
              ))
            )}
          </Stack>
          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            mt={1}
          >
            {/* Las marcas tienen su tabla completa en el registro; duplicar aqui el
                alta y el borrado seria dos sitios donde mantener lo mismo. */}
            {t(
              "company.registry.brandsHint",
              "Las marcas se añaden y se editan en Registro de clientes."
            )}
          </Typography>
        </>
      )}
    </Box>
  );
};

export default CompanyRegistryPanel;
